/**
 * Слежение за выбранной машиной: куда смотреть и насколько близко.
 *
 * Счёт вынесен из карты сюда по той же причине, что и арифметика шторки, —
 * в компоненте его не проверить: там MapLibre, окно браузера и палец.
 * Здесь остаются три решения, ошибка в каждом видна глазом сразу.
 */

import { distanceM } from './geo'

/** Сколько экрана закрыто своим: панель слева, шторка снизу. Пиксели. */
export type Inset = { left: number; bottom: number }

/** Размеры окна карты в пикселях. */
export type Size = { width: number; height: number }

/**
 * Приближение, на котором показывают машину с такой скоростью.
 *
 * Стоящую разглядывают: важно, у какого подъезда она встала. Едущей важно
 * не место, а куда она движется, и чем быстрее — тем дальше вперёд надо
 * видеть: на трассе за пять секунд между ответами сервера машина проезжает
 * больше сотни метров, и на дворовом приближении она бы каждый раз
 * выпрыгивала из кадра.
 */
export function zoomForSpeed(kmh: number): number {
  if (kmh < 5) return 16.5
  if (kmh < 40) return 15.5
  if (kmh < 80) return 14.5
  return 13.5
}

/**
 * Площадь свободной части карты в точках.
 *
 * Машина едет куда хочет, и «сколько дороги видно» — это не ширина и не
 * высота по отдельности, а площадь: она сводит окна любой формы к одному
 * числу, которое можно сравнивать.
 */
function freeArea(inset: Inset, size: Size): number {
  const width = size.width - clamp(inset.left, size.width)
  const height = size.height - clamp(inset.bottom, size.height)
  return Math.max(1, width) * Math.max(1, height)
}

/**
 * Окно, на котором подбирали лестницу приближений, — телефон.
 *
 * iPhone 15 в руке: свободными остаются 353 точки в ширину (393 минус отступ
 * слева) и 662 в высоту (852 минус свёрнутая шторка). Число записано
 * произведением, а не итогом: так видно, откуда оно, и его есть чем
 * пересчитать, если отступы поменяются.
 */
const PHONE_AREA = 353 * 662

/**
 * Насколько ближе можно показать машину в окне побольше телефонного.
 *
 * Приближение задаёт не размер машины на экране, а то, сколько дороги видно
 * вперёд: на этом и стоит лестница по скорости. Но метры на точку от размера
 * окна не зависят — значит вдвое большее окно на том же приближении
 * показывает вдвое больше дороги, чем нужно. На телефоне выбор машины
 * нырял к ней на две ступени, на широком экране — на полступени, и «карта
 * приблизилась» превращалось в «карта чуть дёрнулась»; замерено на одном и
 * том же парке: 12,47 → 14,5 в телефоне против 13,9 → 14,5 на компьютере.
 *
 * Вдвое больше стороны — на ступень ближе: ступень приближения и есть
 * удвоение. Меньше телефона не отдаляем: в узком окне браузера машина
 * уезжала бы от человека вместо того, чтобы приближаться. Больше полутора
 * ступеней не даём: на стене в пять тысяч точек счёт просит подъезда, а
 * там уже не видно ни улицы, ни куда машина едет.
 */
function closerBy(inset: Inset, size: Size): number {
  // Пополам, потому что ступень — это удвоение стороны, а площадь при этом
  // растёт вчетверо.
  const ступени = Math.log2(freeArea(inset, size) / PHONE_AREA) / 2
  return Math.max(0, Math.min(1.5, ступени))
}

/** Приближение для этой машины в этом окне. */
export function zoomFor(kmh: number, inset: Inset, size: Size): number {
  return zoomForSpeed(kmh) + closerBy(inset, size)
}

/**
 * Сколько карты обязано остаться видно, даже если своё закрыло почти всё.
 *
 * Без этого предела раскрытая на весь экран шторка увела бы машину под
 * самый верх окна — в полосу, где её загораживает строка состояния.
 */
const MIN_FREE = 160

/**
 * Насколько сдвинуть машину от середины окна, чтобы она попала в середину
 * свободной части карты. Пара «вправо, вниз» в пикселях — ровно то, что
 * MapLibre ждёт в `offset`.
 *
 * Сдвиг, а не `padding`: padding у MapLibre остаётся в камере навсегда и
 * потом перекашивает всё, что двигает карту после нас.
 */
export function focusOffset(inset: Inset, size: Size): [number, number] {
  const left = clamp(inset.left, size.width)
  const bottom = clamp(inset.bottom, size.height)
  // Половина, а не целое: закрыт только край, и середина оставшейся полосы
  // отстоит от середины окна ровно на половину закрытого.
  const up = bottom / 2
  return [left / 2, up ? -up : 0]
}

function clamp(covered: number, total: number): number {
  return Math.max(0, Math.min(covered, total - MIN_FREE))
}

/**
 * Ближе этого машина считается стоящей на месте, метры.
 *
 * У стоящей машины трекер всё равно шлёт слегка разные точки: спутниковый
 * отсчёт гуляет на несколько метров. Двигать за ним карту — значит трясти
 * её всё время, пока машина ждёт на погрузке.
 */
const STILL_M = 15

type Point = { latitude: number; longitude: number }

/** Стоит ли вести карту к новому положению машины. */
export function movedEnough(from: Point | undefined, to: Point): boolean {
  if (!from) return true
  return distanceM(from.latitude, from.longitude, to.latitude, to.longitude) > STILL_M
}
